import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import type {
  IAuthService,
  IDataService,
  AuthUser,
  ProCustomer,
} from '@erp/data-access/interfaces';
import { queryClient } from './query-client';

interface ProAuthContextValue {
  user: AuthUser | null;
  proCustomer: ProCustomer | null;
  isLoading: boolean;
  /**
   * True tant que le profil pro du compte AUTHENTIFIÉ n'a pas encore été
   * résolu. `proCustomer` vaut alors `null` sans que cela veuille dire
   * « pas de compte pro » : les portillons doivent ATTENDRE. Sans ce drapeau,
   * `setUser()` puis `await hydratePro()` provoquaient un rendu intermédiaire
   * où un électricien pourtant approuvé était renvoyé sur l'écran
   * « Approbation en attente » le temps d'un aller-retour réseau.
   */
  isProCustomerLoading: boolean;
  refreshProCustomer: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  signupAsPro: (input: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    phone: string;
    whatsapp?: string;
    countryCode?: string;
    registrationData?: Record<string, unknown>;
    tenantSubdomain: string;
  }) => Promise<{ requiresEmailConfirmation: boolean }>;
  /** Sends a password reset email. The redirect URL must be registered
   *  in the Supabase auth `uri_allow_list`. We pass the current origin
   *  so the user comes back to the same deployment after clicking. */
  requestPasswordReset: (email: string) => Promise<void>;
  /** Sets a new password on the active session. Used from
   *  /auth/update-password after the user clicks the email link. */
  updatePassword: (newPassword: string) => Promise<void>;
  logout: () => Promise<void>;
  /** True between Supabase emitting PASSWORD_RECOVERY and the new
   *  password being set. Route guards use it to bounce users to
   *  /auth/update-password instead of letting them roam the app. */
  isPasswordRecovery: boolean;
}

const ProAuthContext = createContext<ProAuthContextValue | null>(null);

// ── Owner preview / impersonation handoff ──────────────────────────
// The dashboard opens erp-pro with `?pro_handoff=<one-time token>`. We exchange
// it (edge function + verifyOtp) into a real session before the auth state
// hydrates, then remember the preview context (per-tab) so the layout can show
// a "preview mode" banner with an exit.
const PRO_PREVIEW_KEY = 'pro_preview_session';

interface ProPreviewMeta {
  kind: 'admin_preview' | 'impersonate';
  targetName: string | null;
}

export function readProPreviewSession(): ProPreviewMeta | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(PRO_PREVIEW_KEY);
    return raw ? (JSON.parse(raw) as ProPreviewMeta) : null;
  } catch {
    return null;
  }
}

export function clearProPreviewSession(): void {
  try {
    sessionStorage.removeItem(PRO_PREVIEW_KEY);
  } catch {
    /* noop */
  }
}

async function consumeProHandoffFromUrl(authService: IAuthService): Promise<void> {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  const token = url.searchParams.get('pro_handoff');
  if (!token || !/^[A-Za-z0-9_-]{32,128}$/.test(token)) return;
  try {
    const preview = await authService.exchangeProPlatformHandoff(token);
    if (preview) {
      sessionStorage.setItem(PRO_PREVIEW_KEY, JSON.stringify(preview));
    }
  } catch (err) {
    console.warn('[pro-auth] handoff exchange failed', err);
  } finally {
    url.searchParams.delete('pro_handoff');
    window.history.replaceState({}, '', url.toString());
  }
}

export function ProAuthProvider({
  children,
  authService,
  dataService,
}: {
  children: ReactNode;
  authService: IAuthService;
  dataService: IDataService;
}) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [proCustomer, setProCustomer] = useState<ProCustomer | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false);
  // Id of the account owning the current in-memory query cache. Used to wipe
  // the cache when the authenticated account actually CHANGES so one pro
  // customer never sees another's cached data (no persister here, so this is
  // purely in-memory; the initial session restore, prevId === null, is not a
  // switch and must not clear).
  const lastUserIdRef = useRef<string | null>(null);
  // Compte dont le profil pro est effectivement reflété par `proCustomer`.
  // Tant qu'il est en retard sur `user.id`, l'hydratation est en cours.
  const [proCustomerUserId, setProCustomerUserId] = useState<string | null>(null);
  const proCustomerUserIdRef = useRef<string | null>(null);

  const hydratePro = async (u: AuthUser | null) => {
    if (!u) {
      setProCustomer(null);
      proCustomerUserIdRef.current = null;
      setProCustomerUserId(null);
      return;
    }
    try {
      const pc = await dataService.proCustomers.getCurrent();
      setProCustomer(pc);
    } catch (err) {
      console.warn('[pro-auth] failed to hydrate pro customer', err);
      // Un échec réseau ponctuel ne doit pas se lire comme « aucun compte
      // pro » : on ne jette le profil en cache que s'il appartient à un AUTRE
      // utilisateur. Sinon un approuvé se retrouvait sur l'écran d'attente au
      // moindre hoquet de connexion.
      if (proCustomerUserIdRef.current !== u.id) setProCustomer(null);
    } finally {
      proCustomerUserIdRef.current = u.id;
      setProCustomerUserId(u.id);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Exchange an owner preview/impersonation handoff token (if present in the
      // launch URL) into a session BEFORE we read the current user, so the
      // hydrate below already reflects the previewed identity.
      await consumeProHandoffFromUrl(authService);
      const u = await authService.getCurrentUser();
      if (cancelled) return;
      lastUserIdRef.current = u?.id ?? null;
      setUser(u);
      await hydratePro(u);
      setIsLoading(false);
    })();
    const unsubscribe = authService.onAuthStateChange(async (u) => {
      const nextId = u?.id ?? null;
      const prevId = lastUserIdRef.current;
      // A switch to a different pro account must not inherit the previous
      // account's cached data. Only clear on a genuine A → B change; the
      // initial restore (prevId === null) is the same account hydrating.
      if (prevId !== null && nextId !== null && prevId !== nextId) {
        queryClient.clear();
      }
      lastUserIdRef.current = nextId;
      setUser(u);
      await hydratePro(u);
      setIsLoading(false);
    });
    // PASSWORD_RECOVERY listener — fires once when Supabase auth picks
    // up a recovery token from the URL hash. Surfaces a flag so the
    // App can redirect to /auth/update-password.
    const unsubRecovery = authService.onPasswordRecovery(() => {
      setIsPasswordRecovery(true);
    });
    return () => {
      cancelled = true;
      unsubscribe();
      unsubRecovery();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authService, dataService]);

  const refreshProCustomer = async () => {
    await hydratePro(user);
  };

  const login = async (email: string, password: string) => {
    const session = await authService.login({ email, password });
    // Fresh sign-in starts from a clean cache — never inherit the previous
    // account's in-memory queries.
    queryClient.clear();
    lastUserIdRef.current = session.user.id;
    setUser(session.user);
    await hydratePro(session.user);
  };

  const signupAsPro: ProAuthContextValue['signupAsPro'] = async (input) => {
    const session = await authService.signupAsPro({
      email: input.email,
      password: input.password,
      firstName: input.firstName,
      lastName: input.lastName,
      tenantSubdomain: input.tenantSubdomain,
      metadata: {
        phone: input.phone,
        whatsapp: input.whatsapp,
        country_code: input.countryCode,
      },
    });
    if (!session) {
      // Ne devrait plus arriver : le compte est créé déjà confirmé côté
      // serveur. Garde-fou si l'ouverture de session échoue quand même —
      // l'appelant renvoie vers /login plutôt que de laisser un compte sans
      // profil pro (l'ancien bug : invisible de la file d'approbation).
      return { requiresEmailConfirmation: true };
    }
    queryClient.clear();
    lastUserIdRef.current = session.user.id;
    setUser(session.user);
    await dataService.proCustomers.createSelf({
      email: input.email,
      firstName: input.firstName,
      lastName: input.lastName,
      phone: input.phone,
      whatsapp: input.whatsapp ?? '',
      countryCode: input.countryCode ?? '+216',
      registrationData: input.registrationData ?? {},
      tenantSubdomain: input.tenantSubdomain,
    });
    await hydratePro(session.user);
    return { requiresEmailConfirmation: false };
  };

  const requestPasswordReset = async (email: string) => {
    // Send the user back to /auth/update-password (not /login) so they
    // actually get a UI to set their new password instead of a session
    // sitting silently in recovery state on the login screen.
    await authService.requestPasswordReset(
      email,
      `${window.location.origin}/auth/update-password`,
    );
  };

  const updatePassword = async (newPassword: string) => {
    await authService.updatePassword(newPassword);
    setIsPasswordRecovery(false);
  };

  const logout = async () => {
    clearProPreviewSession();
    try {
      await authService.logout();
    } catch (err) {
      console.warn('[pro-auth] logout server signOut failed', err);
    }
    queryClient.clear();
    lastUserIdRef.current = null;
    setUser(null);
    setProCustomer(null);
    proCustomerUserIdRef.current = null;
    setProCustomerUserId(null);
    setIsPasswordRecovery(false);
  };

  return (
    <ProAuthContext.Provider
      value={{
        user,
        proCustomer,
        isLoading,
        isProCustomerLoading: user !== null && proCustomerUserId !== user.id,
        refreshProCustomer,
        login,
        signupAsPro,
        requestPasswordReset,
        updatePassword,
        logout,
        isPasswordRecovery,
      }}
    >
      {children}
    </ProAuthContext.Provider>
  );
}

export function useProAuth(): ProAuthContextValue {
  const ctx = useContext(ProAuthContext);
  if (!ctx) throw new Error('useProAuth must be used within ProAuthProvider');
  return ctx;
}
